import { Shield, Lock, Database, Globe, Cookie, User, Mail } from "lucide-react";

function Privacy() {
  return (
    <div className="min-h-screen bg-[#05070c] text-white">

      {/* soft background glow like your screenshot */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute bottom-[-220px] right-[-160px] h-[520px] w-[520px] rounded-full bg-sky-500/6 blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/40" />
      </div>

      <main className="px-3 sm:px-6 md:px-8 pt-6 pb-12">
        <div className="mx-auto max-w-6xl">
          {/* Outer framed panel */}
          <section className="rounded-[28px] border border-white/12 bg-white/4 shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_12px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl">
            {/* Header */}
            <div className="border-b border-white/10 px-5 sm:px-8 py-6 sm:py-7">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-2xl border border-white/12 bg-white/5">
                  <Shield className="text-emerald-300" size={22} />
                </div>

                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                    Privacy{" "}
                    <span className="text-emerald-300 drop-shadow-[0_0_18px_rgba(16,185,129,0.15)]">
                      Policy
                    </span>
                  </h1>
                  <p className="mt-1 text-sm sm:text-base text-white/70">
                    This explains what AnimeHouse collects, how we use it, and what choices you have.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {["Account", "Security", "Cookies", "Your Rights"].map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs font-semibold text-white/75"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <p className="mt-4 text-xs text-white/45">
                    Last Updated: January 2026
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 sm:px-8 py-6 sm:py-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Sticky mini nav (desktop only) */}
                <aside className="lg:col-span-4">
                  <div className="lg:sticky lg:top-24 rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6">
                    <p className="text-sm font-semibold text-emerald-200">On this page</p>

                    <nav className="mt-4 space-y-2 text-sm">
                      {[
                        { href: "#intro", label: "Introduction", icon: Lock },
                        { href: "#collect", label: "Information We Collect", icon: Database },
                        { href: "#extension", label: "Browser Extension", icon: Globe },
                        { href: "#use", label: "How We Use It", icon: User },
                        { href: "#security", label: "Storage & Security", icon: Lock },
                        { href: "#third", label: "Third-Party Services", icon: Globe },
                        { href: "#cookies", label: "Cookies", icon: Cookie },
                        { href: "#rights", label: "Your Rights", icon: User },
                        { href: "#contact", label: "Contact", icon: Mail },
                      ].map(({ href, label, icon: Icon }) => (
                        <a
                          key={href}
                          href={href}
                          className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/4 px-4 py-3 text-white/70 hover:text-white hover:bg-white/6 transition"
                        >
                          <span className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5">
                            <Icon size={16} className="text-emerald-200/80 group-hover:text-emerald-200" />
                          </span>
                          <span className="font-medium">{label}</span>
                        </a>
                      ))}
                    </nav>

                    <div className="mt-5 rounded-2xl border border-emerald-400/18 bg-emerald-500/8 px-4 py-3 text-xs text-emerald-100/80">
                      We don’t sell personal info. We use it to run the app and improve the experience.
                    </div>
                  </div>
                </aside>

                {/* Main sections */}
                <div className="lg:col-span-8 space-y-4">
                  {/* Section Card helper */}
                  <SectionCard
                    id="intro"
                    title="Introduction"
                    icon={<Lock className="text-emerald-300" size={18} />}
                  >
                    <p className="leading-relaxed text-white/80">
                      This Privacy Policy describes how AnimeHouse (“we”, “us”, or “our”) collects,
                      uses, and shares information when you use our application.
                    </p>
                  </SectionCard>

                  <SectionCard
                    id="collect"
                    title="Information We Collect"
                    icon={<Database className="text-emerald-300" size={18} />}
                  >
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-emerald-200 mb-2">
                          Information You Provide
                        </h3>
                        <ul className="space-y-2 text-white/80">
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Account:</span>{" "}
                            email, username, password (encrypted)
                          </li>
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Profile:</span>{" "}
                            bio, profile picture
                          </li>
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Content:</span>{" "}
                            reviews, ratings, comments, favorites
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-emerald-200 mb-2">
                          Automatically Collected
                        </h3>
                        <ul className="space-y-2 text-white/80">
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Usage:</span>{" "}
                            pages visited, features used, time spent
                          </li>
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Device:</span>{" "}
                            IP address, browser type, device type
                          </li>
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Cookies:</span>{" "}
                            session + preferences
                          </li>
                        </ul>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    id="extension"
                    title="Browser Extension"
                    icon={<Globe className="text-emerald-300" size={18} />}
                  >
                    <div className="space-y-4">
                      <p className="text-white/80 leading-relaxed">
                        Our browser extension enables synchronized watch parties on streaming platforms.
                      </p>

                      <div>
                        <h3 className="text-sm font-semibold text-emerald-200 mb-2">
                          Extension Data Collection
                        </h3>
                        <ul className="space-y-2 text-white/80">
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Local Storage:</span>{" "}
                            party ID, user ID, username, host status (stored only in your browser)
                          </li>
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Video Playback Data:</span>{" "}
                            current time, play/pause state, video URL (transmitted only during active watch parties)
                          </li>
                          <li className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                            <span className="font-semibold text-white/90">Platform Access:</span>{" "}
                            Netflix, Crunchyroll, Hulu, Disney+, Funimation (only to detect and control video players)
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-emerald-400/18 bg-emerald-500/8 px-4 py-3 text-sm text-emerald-100/80">
                        <p className="font-semibold mb-1">Privacy Note:</p>
                        <p>
                          The extension does NOT record, store, or transmit any video content. It only synchronizes
                          playback controls. All watch party data is deleted when you disconnect from the party.
                        </p>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    id="use"
                    title="How We Use Your Information"
                    icon={<User className="text-emerald-300" size={18} />}
                  >
                    <ul className="space-y-2 text-white/80">
                      {[
                        "Provide and maintain our services",
                        "Authenticate users and prevent fraud",
                        "Personalize your experience",
                        "Send notifications about reviews, responses, and updates",
                        "Analyze usage patterns to improve our services",
                        "Comply with legal obligations",
                      ].map((t) => (
                        <li
                          key={t}
                          className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3"
                        >
                          {t}
                        </li>
                      ))}
                    </ul>
                  </SectionCard>

                  <SectionCard
                    id="security"
                    title="Data Storage & Security"
                    icon={<Lock className="text-emerald-300" size={18} />}
                  >
                    <ul className="space-y-2 text-white/80">
                      {[
                        "Encryption: passwords are hashed using bcrypt",
                        "Database: data stored on Neon PostgreSQL with SSL/TLS",
                        "Access control: limited to authorized personnel",
                        "Rate limiting: protection against brute force attempts",
                        "Security headers: Helmet.js for HTTP hardening",
                      ].map((t) => (
                        <li
                          key={t}
                          className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3"
                        >
                          {t}
                        </li>
                      ))}
                    </ul>
                  </SectionCard>

                  <SectionCard
                    id="third"
                    title="Third-Party Services"
                    icon={<Globe className="text-emerald-300" size={18} />}
                  >
                    <div className="space-y-3">
                      <ServiceCard
                        title="Neon Database"
                        purpose="Data storage"
                        shared="All application data"
                        href="https://neon.tech/privacy-policy"
                      />
                      <ServiceCard
                        title="Google reCAPTCHA"
                        purpose="Bot protection"
                        shared="IP address, browser information"
                        href="https://policies.google.com/privacy"
                      />
                      <ServiceCard
                        title="Firebase"
                        purpose="Authentication"
                        shared="Authentication credentials, email addresses"
                        href="https://firebase.google.com/support/privacy"
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    id="cookies"
                    title="Cookies"
                    icon={<Cookie className="text-emerald-300" size={18} />}
                  >
                    <div className="space-y-3 text-white/80">
                      <p>We use cookies to:</p>
                      <ul className="space-y-2">
                        {["Maintain sessions", "Remember preferences", "Analyze usage patterns"].map(
                          (t) => (
                            <li
                              key={t}
                              className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3"
                            >
                              {t}
                            </li>
                          )
                        )}
                      </ul>
                      <p className="text-white/60 text-sm">
                        You can control cookies through your browser settings.
                      </p>
                    </div>
                  </SectionCard>

                  <SectionCard
                    id="rights"
                    title="Your Rights"
                    icon={<User className="text-emerald-300" size={18} />}
                  >
                    <ul className="space-y-2 text-white/80">
                      {[
                        "Access: request a copy of your personal data",
                        "Rectification: correct inaccurate data",
                        "Erasure: request deletion of your data",
                        "Portability: receive your data in a portable format",
                        "Object: object to processing of your data",
                        "Withdraw consent: withdraw at any time",
                      ].map((t) => (
                        <li
                          key={t}
                          className="rounded-2xl border border-white/10 bg-white/4 px-4 py-3"
                        >
                          {t}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/4 px-4 py-3 text-sm text-white/70">
                      <p className="font-semibold text-white/85 mb-1">
                        Children’s Privacy
                      </p>
                      <p>
                        AnimeHouse is not intended for users under 13. We do not knowingly collect
                        data from children under 13.
                      </p>
                    </div>
                  </SectionCard>

                  <SectionCard
                    id="contact"
                    title="Contact"
                    icon={<Mail className="text-emerald-300" size={18} />}
                  >
                    <p className="text-white/80">
                      Questions about this policy? Reach us at:
                    </p>

                    <div className="mt-3 rounded-2xl border border-white/10 bg-white/4 px-4 py-3">
                      <p className="text-sm text-white/70">
                        <span className="font-semibold text-white/90">Email:</span>{" "}
                        <a
                          href="mailto:animehousewebsite@gmail.com"
                          className="text-emerald-200 hover:underline"
                        >
                          animehousewebsite@gmail.com
                        </a>
                      </p>
                    </div>

                    <div className="mt-4 rounded-2xl border border-emerald-400/18 bg-emerald-500/8 px-4 py-3 text-sm text-emerald-100/80">
                      By using AnimeHouse, you acknowledge you’ve read and understood this Privacy Policy.
                    </div>
                  </SectionCard>
                </div>
              </div>
            </div>

            {/* footer line */}
            <div className="border-t border-white/10 px-6 sm:px-8 py-4 text-center text-xs text-white/35">
              Built with love for anime — and for the people who never stop talking about it.
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function SectionCard({ id, title, icon, children }) {
  return (
    <section
      id={id}
      className="scroll-mt-28 rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6"
    >
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
          {icon}
        </div>
        <h2 className="text-lg sm:text-xl font-bold text-emerald-200">{title}</h2>
      </div>

      <div className="mt-4">{children}</div>
    </section>
  );
}

function ServiceCard({ title, purpose, shared, href }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/4 px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-emerald-200">{title}</p>
          <p className="text-sm text-white/70 mt-1">
            <span className="font-semibold text-white/85">Purpose:</span> {purpose}
          </p>
          <p className="text-sm text-white/70">
            <span className="font-semibold text-white/85">Data Shared:</span> {shared}
          </p>
        </div>

        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-xl border border-white/12 bg-white/5 px-3 py-2 text-xs font-semibold text-white/70 hover:bg-white/8"
        >
          View
        </a>
      </div>
    </div>
  );
}

export default Privacy;
